import { DOCCheckIcon } from "@/components/common/icons/DOCCheckIcon/DOCCheckIcon"
import { DOCSparkleIcon } from "@/components/common/icons/DOCSparkleIcon/DOCSparkleIcon"
import { DocumentExchangeIcon } from "@/components/common/icons/DocumentExchangeIcon/DocumentExchangeIcon"
import { PDFCheckIcon } from "@/components/common/icons/PDFCheckIcon/PDFCheckIcon"
import { PDFSparkleIcon } from "@/components/common/icons/PDFSparkleIcon/PDFSparkleIcon"
import { StatusCompletedIcon } from "@/components/common/icons/StatusCompletedIcon/StatusCompletedIcon"
import { StatusInProgressIcon } from "@/components/common/icons/StatusInProgressIcon/StatusInProgressIcon"
import { XLSCheckIcon } from "@/components/common/icons/XLSCheckIcon/XLSCheckIcon"
import { XLSIcon } from "@/components/common/icons/XLSIcon/XLSIcon"
import { XLSSparkleIcon } from "@/components/common/icons/XLSSparkleIcon/XLSSparkleIcon"
import { FeatureFlagMap } from "@/types/featureFlag"
import { QuestionnaireStatus } from "@/types/questionnaire/models"
import { DEFAULT_FLAGS } from "@/utils/featureFlags"
import { isExcelFileName } from "@/utils/fileUtils"
import { IconCircleX } from "@tabler/icons-react"
import { createElement } from "react"

const StatusFailedIcon = () =>
  createElement(IconCircleX, {
    size: 25,
    stroke: 1.2,
    className: "text-red1",
  })

export const getStatusIconByStatus = (
  status: QuestionnaireStatus,
): React.ElementType => {
  switch (status) {
    case QuestionnaireStatus.New:
    case QuestionnaireStatus.Extracting:
    case QuestionnaireStatus.Extracted:
    case QuestionnaireStatus.GeneratingAnswers:
    case QuestionnaireStatus.EditAnswers:
      return StatusInProgressIcon
    case QuestionnaireStatus.Completed:
      return StatusCompletedIcon
    case QuestionnaireStatus.Failed:
      return StatusFailedIcon
    default:
      return StatusInProgressIcon
  }
}

export const getFileIconByStatusAndName = (
  status?: QuestionnaireStatus,
  fileName?: string,
): React.ElementType => {
  switch (status) {
    case QuestionnaireStatus.New:
    case QuestionnaireStatus.Extracting:
    case QuestionnaireStatus.Extracted:
    case QuestionnaireStatus.GeneratingAnswers:
      if (fileName?.endsWith(".xls") || fileName?.endsWith(".xlsx")) {
        return XLSCheckIcon
      } else if (fileName?.endsWith(".pdf")) {
        return PDFCheckIcon
      } else if (fileName?.endsWith(".docx")) {
        return DOCCheckIcon
      } else {
        return XLSIcon
      }
    case QuestionnaireStatus.EditAnswers:
    case QuestionnaireStatus.Completed:
      if (fileName?.endsWith(".xls") || fileName?.endsWith(".xlsx")) {
        return XLSSparkleIcon
      } else if (fileName?.endsWith(".pdf")) {
        return PDFSparkleIcon
      } else if (fileName?.endsWith(".docx")) {
        return DOCSparkleIcon
      } else {
        return XLSIcon
      }
    case QuestionnaireStatus.Failed:
      return DocumentExchangeIcon
    default:
      return DocumentExchangeIcon
  }
}

export const getRouteByStatus = (
  questionnaireId: string,
  status: QuestionnaireStatus,
  fileName?: string,
  hasPositionData?: boolean,
  taskGroupId?: string,
  flags?: Pick<FeatureFlagMap, "EXCEL_INTELLIGENCE" | "PDF_INTELLIGENCE">,
): string => {
  const resolvedFlags = flags ?? DEFAULT_FLAGS
  const url = `/questionnaire/${questionnaireId}`
  const isExcelIntelligence =
    resolvedFlags.EXCEL_INTELLIGENCE && fileName
      ? isExcelFileName(fileName) && hasPositionData
      : false

  const isPdfIntelligence =
    resolvedFlags.PDF_INTELLIGENCE && fileName
      ? fileName.endsWith(".pdf") && hasPositionData
      : false

  switch (status) {
    case QuestionnaireStatus.New:
    case QuestionnaireStatus.Extracting:
      return url + "/processing"
    case QuestionnaireStatus.Extracted:
      if (isPdfIntelligence) {
        return url + "/pdf-questions"
      }
      if (isExcelIntelligence) {
        return url + "/excel-questions"
      }
      return url + "/questions"
    case QuestionnaireStatus.GeneratingAnswers:
      if (taskGroupId === undefined) {
        return url + "/questions"
      }
      return url + `/auto-fill/${taskGroupId}`
    case QuestionnaireStatus.EditAnswers:
      if (hasPositionData) {
        if (isPdfIntelligence) {
          return url + "/pdf-answers"
        } else if (isExcelIntelligence) {
          return url + "/excel-answers"
        } else {
          return url + "/answers"
        }
      }
      return url + "/answers"
    case QuestionnaireStatus.Completed:
      return url + "/preview"
    case QuestionnaireStatus.Failed:
      return url + "/questions"
    default:
      return url + "/questions"
  }
}

export const getStatusByRoute = (route: string): QuestionnaireStatus => {
  if (route.endsWith("/questions") || route.endsWith("/processing")) {
    return QuestionnaireStatus.New
  } else if (
    route.endsWith("/answers") ||
    route.endsWith("/excel-answers") ||
    route.endsWith("/pdf-answers")
  ) {
    return QuestionnaireStatus.EditAnswers
  } else if (route.endsWith("/preview")) {
    return QuestionnaireStatus.Completed
  } else if (route.match(/\/questionnaire\/\w+\/auto-fill\/\w+$/)) {
    return QuestionnaireStatus.GeneratingAnswers
  } else {
    return QuestionnaireStatus.New
  }
}
